/*
   Instrucciones para revisar el estado/capacidad de la batería según la marca del dispositivo.
   Fuente: "Manual de verificación de salud de batería" (Reuse · Chile · Julio 2026)
   https://apps-trade-in.s3.us-west-2.amazonaws.com/assets/Captura+Estado+Bateri%CC%81a.pdf

   Para agregar imágenes: subirlas a S3 en la carpeta indicada por BATTERY_ASSETS_BASE
   y referenciarlas en el paso, por ejemplo: image: `${BATTERY_ASSETS_BASE}/apple-1.png`
   También acepta imágenes locales importadas desde '@/assets/images/...'
 */

export const BATTERY_ASSETS_BASE = `https://${import.meta.env.VITE_BUCKET}.s3.us-west-2.amazonaws.com/bateria/`

const URL_INSTRUCTIONS_PDF = `https://${import.meta.env.VITE_BUCKET}.s3.us-west-2.amazonaws.com/assets/Captura+Estado+Bateri%CC%81a.pdf`

/* Criterio único del manual: solo se registra si la salud está sobre o bajo 80% */
export const BATTERY_CRITERIA = '' // 'Ambos casos son aptos: solo se recoge el dato de si está sobre o bajo 80%.'

/* Aclaración para evitar el error más común al registrar el dato */
export const BATTERY_HEALTH_VS_CHARGE = 'El estado de la batería es diferente al porcentaje de carga actual del dispositivo.'

/*
 native   = el panel con % está en todo el catálogo vigente
 software = depende de la versión de software, no del modelo (verificar en el equipo)
 none     = ningún modelo de la marca entrega % de salud
 */
export type TBatteryRegistry = 'native' | 'software' | 'none'

export interface IBatteryStep {
  title: string
  description?: string
  /* URL (S3) o import local de la imagen de apoyo del paso */
  image?: string
  /* Texto destacado al pie del paso (rutas alternativas, advertencias, etc.) */
  note?: string
}

export interface IBatteryInstructions {
  brand: string
  /* Nombre que se muestra en el encabezado, ej. "Apple iPhone" */
  title: string
  registry: TBatteryRegistry
  /* Etiqueta del manual: "% de salud nativo" / "Depende del software" / "Sin registro" */
  registryLabel: string
  summary: string
  steps: IBatteryStep[]
  /* Homologación de estados a sobre/bajo 80% (Samsung no entrega %) */
  stateMapping?: Array<{ state: string, result: string }>
  /* Modelos donde el panel está disponible */
  supported?: { label: string, models?: string[], note?: string }
  /* Modelos donde el panel no aparece */
  unsupported?: { label: string, note: string }
  pdfUrl?: string
}

const appleInstructions: IBatteryInstructions = {
  brand: 'Apple',
  title: 'Apple iPhone',
  registry: 'native',
  registryLabel: '% de salud nativo',
  summary: 'Muestra el % directo. Disponible desde iOS 11.3 (iPhone 6s en adelante).',
  steps: [
    { title: 'Abrir Ajustes → Batería' },
    { title: 'Tocar Salud y carga de la batería' },
    { title: 'Leer Capacidad máxima (%) y marcar sobre / bajo 80%' }
  ],
  supported: {
    label: 'Funciona en · todo el catálogo',
    models: ['iPhone 6s', '7 · 8', 'X · XR · XS', 'SE 2.ª / 3.ª', '11 · 12 · 13', '14 · 15 · 16', '17 · Air'],
    note: 'Prácticamente todo iPhone que llega a Trade-in muestra el %.'
  },
  pdfUrl: URL_INSTRUCTIONS_PDF
}

const samsungInstructions: IBatteryInstructions = {
  brand: 'Samsung',
  title: 'Samsung Galaxy',
  registry: 'software',
  registryLabel: 'Depende del software',
  summary: 'Samsung entrega un estado (Normal / Débil / Malo), no un %. Se homologa según la tabla.',
  steps: [
    { title: 'Abrir Samsung Members', description: 'Viene preinstalada en el dispositivo.' },
    { title: 'Ir a Soporte → Diagnóstico del teléfono' },
    { title: 'Ejecutar Estado de la batería', description: 'Leer el resultado: Normal / Débil / Malo.' }
  ],
  stateMapping: [
    { state: 'Normal', result: 'Sobre 80%' },
    { state: 'Débil', result: 'Bajo 80%' },
    { state: 'Malo', result: 'Bajo 80%' }
  ],
  supported: {
    label: 'Funciona en',
    models: ['Serie S (S9 →)', 'Serie A', 'Serie M', 'Note', 'Z Fold / Flip']
  },
  pdfUrl: URL_INSTRUCTIONS_PDF
}

const xiaomiInstructions: IBatteryInstructions = {
  brand: 'Xiaomi',
  title: 'Xiaomi / Redmi / POCO',
  registry: 'software',
  registryLabel: 'Depende del software',
  summary: 'El % llegó con HyperOS 2. Depende de la versión instalada, no del modelo.',
  steps: [
    { title: 'Ajustes → Acerca del teléfono', description: 'Confirmar que tenga HyperOS 2 o superior.' },
    { title: 'Ir a Ajustes → Batería → Protección de batería' },
    { title: 'Leer Salud de batería (%) y marcar sobre / bajo 80%' }
  ],
  supported: {
    label: 'Sí lo muestran (HyperOS 2+)',
    models: ['Xiaomi 14 · 15', '13T', 'Redmi Note 13 · 14', 'POCO F6 · X6']
  },
  unsupported: {
    label: 'No lo muestran',
    note: 'Equipos en MIUI o HyperOS 1, aunque sean recientes. Verificar en el equipo.'
  },
  pdfUrl: URL_INSTRUCTIONS_PDF
}

const huaweiInstructions: IBatteryInstructions = {
  brand: 'Huawei',
  title: 'Huawei',
  registry: 'software',
  registryLabel: 'Depende del software',
  summary: 'Capacidad máxima desde HarmonyOS 4 / EMUI 14, con despliegue por lotes.',
  steps: [
    { title: 'Abrir Ajustes → Batería' },
    { title: 'Revisar si aparece Capacidad máxima' },
    { title: 'Si aparece, leer el % y marcar sobre / bajo 80%' }
  ],
  supported: {
    label: 'Puede aparecer en (HarmonyOS 4 / EMUI 14+)',
    models: ['Mate 60 · 70', 'Pura 70 · 80', 'Nova 13 · 14']
  },
  unsupported: {
    label: 'Ojo',
    note: 'Despliegue por lotes: dos unidades del mismo modelo pueden diferir. Verificar en el equipo.'
  },
  pdfUrl: URL_INSTRUCTIONS_PDF
}

const honorInstructions: IBatteryInstructions = {
  brand: 'Honor',
  title: 'Honor',
  registry: 'software',
  registryLabel: 'Solo serie Magic',
  summary: 'El % solo está confirmado en la serie Magic (MagicOS 7.1 / 8).',
  steps: [
    { title: 'Abrir Ajustes → Batería' },
    { title: 'Revisar si aparece Salud de la batería' },
    { title: 'Si aparece, leer el % y marcar sobre / bajo 80%' }
  ],
  supported: {
    label: 'Confirmado en',
    models: ['Magic5', 'Magic6']
  },
  unsupported: {
    label: 'No confirmado',
    note: 'Serie X de gama media (X5 al X10). Verificar en el equipo si aparece la opción.'
  },
  pdfUrl: URL_INSTRUCTIONS_PDF
}

const motorolaInstructions: IBatteryInstructions = {
  brand: 'Motorola',
  title: 'Motorola',
  registry: 'native',
  registryLabel: '% de salud nativo',
  summary: 'Panel nativo con estado y ciclos, en modelos con Android reciente.',
  steps: [
    { title: 'Abrir Ajustes → Batería' },
    { title: 'Tocar Información de la batería' },
    { title: 'Leer el Estado y marcar sobre / bajo 80%' }
  ],
  supported: {
    label: 'Funciona en',
    models: ['Moto G (recientes)', 'Edge', 'Razr']
  },
  unsupported: {
    label: 'No funciona',
    note: 'Equipos muy antiguos o con Android desactualizado que no muestren esta pantalla.'
  },
  pdfUrl: URL_INSTRUCTIONS_PDF
}

/* Marcas donde ningún modelo entrega % de salud: no hay dato que recoger */
const noRegistryInstructions = (brand: string, title: string, summary: string): IBatteryInstructions => ({
  brand,
  title,
  registry: 'none',
  registryLabel: 'Sin registro',
  summary,
  steps: [],
  pdfUrl: URL_INSTRUCTIONS_PDF
})

const oppoInstructions = noRegistryInstructions('Oppo', 'Oppo', 'ColorOS no muestra el % en ningún modelo (hasta ColorOS 15: Find X8, Reno14).')
const vivoInstructions = noRegistryInstructions('Vivo', 'Vivo', 'Ni OriginOS/Funtouch ni iManager entregan un % consistente. Series Y, V, X e iQOO.')
const realmeInstructions = noRegistryInstructions('Realme', 'Realme', 'Misma base ColorOS que Oppo: ningún modelo entrega %.')
const infinixInstructions = noRegistryInstructions('Infinix', 'Infinix', 'XOS no tiene panel de salud en ningún modelo (Hot, Note, Smart, Zero).')

/* Fallback para marcas que no están en el manual */
const genericInstructions: IBatteryInstructions = {
  brand: 'Otras marcas',
  title: 'Otras marcas',
  registry: 'software',
  registryLabel: 'Verificar en el equipo',
  summary: 'Revisar en el equipo si existe panel de salud de batería.',
  steps: [
    { title: 'Abrir Ajustes → Batería' },
    { title: 'Buscar Salud de la batería, Capacidad máxima o Información de la batería' },
    {
      title: 'Si aparece, leer el % y marcar sobre / bajo 80%'
      // note: 'Si el equipo no muestra el dato, márcalo como «sin dato de salud».'
    }
  ],
  pdfUrl: URL_INSTRUCTIONS_PDF
}

/**
 * Devuelve las instrucciones correspondientes a la marca recibida.
 * Si la marca no está en el manual, retorna la guía genérica.
 */
export const getBatteryInstructions = (brand?: string): IBatteryInstructions => {
  switch ((brand ?? '').trim().toLowerCase()) {
    case 'apple':
      return appleInstructions
    case 'samsung':
      return samsungInstructions
    case 'xiaomi':
    case 'redmi':
    case 'poco':
      return xiaomiInstructions
    case 'huawei':
      return huaweiInstructions
    case 'honor':
      return honorInstructions
    case 'motorola':
      return motorolaInstructions
    case 'oppo':
      return oppoInstructions
    case 'vivo':
      return vivoInstructions
    case 'realme':
      return realmeInstructions
    case 'infinix':
      return infinixInstructions
    default:
      return genericInstructions
  }
}
